import React from "react";
import type { EnrolmentFormState } from "../useEnrolmentForm";
import type { PlanField } from "../../../../api/academyEnrolments/enrolmentsService";

interface SummaryAsideProps {
  state: EnrolmentFormState;
  mode: "add" | "edit" | "view";
  planFields: PlanField[];
}

const FREQ_LABELS: Record<string, string> = {
  ONCE_A_WEEK: "1x/week",
  TWICE_A_WEEK: "2x/week",
  THRICE_A_WEEK: "3x/week",
  FOUR_TIMES_A_WEEK: "4x/week",
  FIVE_TIMES_A_WEEK: "5x/week",
  SIX_TIMES_A_WEEK: "6x/week",
  DAILY: "Daily",
};

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="enrolment-summary-row">
      <span className="enrolment-summary-label">{label}</span>
      <span className="enrolment-summary-value">{value}</span>
    </div>
  );
}

const SummaryAside: React.FC<SummaryAsideProps> = ({ state, mode, planFields }) => {
  const {
    selectedPlayer,
    selectedAcademy,
    selectedSport,
    selectedCenter,
    selectedAgeCategory,
    selectedBatch,
    selectedSlot,
    selectedTrainingFrequency,
    selectedPaymentFrequency,
    startDate,
    registerBy,
    searchedProfileData,
    paymentAmount,
  } = state;

  const slotDisplay = (() => {
    if (!selectedSlot) return null;
    if (selectedSlot.days) {
      return `${selectedSlot.days.map((d) => d.charAt(0).toUpperCase() + d.slice(1, 3)).join(", ")} • ${selectedSlot.startTime}–${selectedSlot.endTime}`;
    }
    if (selectedSlot.day) {
      return `${selectedSlot.day.charAt(0).toUpperCase() + selectedSlot.day.slice(1)} • ${selectedSlot.startTime}–${selectedSlot.endTime}`;
    }
    return null;
  })();

  const feeAmount = (() => {
    if (!state.selectedFee || !state.selectedPaymentFrequency) return null;
    const field = planFields.find((f) => f.frequency === state.selectedPaymentFrequency);
    const amount = field ? state.selectedFee.feeInfo[field.feeKey] : null;
    if (amount == null) return null;
    return `₹${amount.toLocaleString("en-IN")}`;
  })();

  const paymentFrequencyLabel =
    planFields.find((field) => field.frequency === selectedPaymentFrequency)?.label ??
    selectedPaymentFrequency;

  const hasAnySelection =
    selectedPlayer ||
    selectedAcademy ||
    selectedSport ||
    selectedCenter ||
    selectedAgeCategory ||
    selectedBatch ||
    selectedSlot ||
    selectedTrainingFrequency ||
    selectedPaymentFrequency ||
    startDate ||
    paymentAmount;

  return (
    <div className="enrolment-summary-aside">
      <div className="enrolment-summary-header">
        <i className="ri-file-list-3-line me-2" />
        Enrolment Summary
      </div>

      {!hasAnySelection ? (
        <p className="text-muted mb-0" style={{ fontSize: 13 }}>
          Fill in the form to see a summary here.
        </p>
      ) : (
        <div className="enrolment-summary-body">
          {searchedProfileData && (
            <Row label="Phone" value={`+91 ${searchedProfileData.phoneNumber}`} />
          )}
          {selectedPlayer && (
            <Row label="Player" value={selectedPlayer.fullName} />
          )}
          {registerBy && (
            <Row label="Enrolled By" value={registerBy === "self" ? "Self" : "Parent"} />
          )}
          {selectedAcademy && (
            <Row label="Academy" value={selectedAcademy.basicDetails.academyName} />
          )}
          {selectedSport && <Row label="Sport" value={selectedSport} />}
          {selectedCenter && (
            <Row label="Center" value={selectedCenter.basicDetails.location} />
          )}
          {selectedAgeCategory && (
            <Row label="Age Category" value={selectedAgeCategory} />
          )}
          {selectedBatch && (
            <>
              <Row label="Batch" value={selectedBatch.batchName} />
              <Row label="Slot Type" value={selectedBatch.slotType === "fixed" ? "Fixed" : "Dynamic"} />
            </>
          )}
          {slotDisplay && <Row label="Slot" value={slotDisplay} />}
          {selectedTrainingFrequency && (
            <Row
              label="Training"
              value={FREQ_LABELS[selectedTrainingFrequency] ?? selectedTrainingFrequency}
            />
          )}
          {selectedPaymentFrequency && (
            <Row
              label="Payment Plan"
              value={paymentFrequencyLabel}
            />
          )}
          {feeAmount && <Row label="Fee" value={feeAmount} />}
          {startDate && <Row label="Start Date" value={startDate} />}
          {paymentAmount && parseFloat(paymentAmount) > 0 && (
            <Row
              label="Payment Amount"
              value={`₹${parseFloat(paymentAmount).toLocaleString("en-IN")}`}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SummaryAside;
